// Calculate uptime based on birthday
export const calculateUptime = (birthday) => {
  if (!birthday) return null;
  
  try {
    const birthDate = new Date(birthday);
    const currentDate = new Date();
    
    // Validate birth date
    if (isNaN(birthDate.getTime()) || birthDate > currentDate) {
      return null;
    }
    
    // Calculate the difference
    let years = currentDate.getFullYear() - birthDate.getFullYear();
    let months = currentDate.getMonth() - birthDate.getMonth();
    let days = currentDate.getDate() - birthDate.getDate();
    
    // Adjust for negative days
    if (days < 0) {
      months--;
      // Get the last day of the previous month
      const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
      days += lastMonth.getDate();
    }
    
    // Adjust for negative months
    if (months < 0) {
      years--;
      months += 12;
    }
    
    return {
      years,
      months,
      days
    };
  } catch (error) {
    console.error('Error calculating uptime:', error);
    return null;
  }
};

// Format uptime for display
export const formatUptime = (birthday) => {
  const uptime = calculateUptime(birthday);
  if (!uptime) return null;
  
  const { years, months, days } = uptime;
  
  const parts = [];
  if (years > 0) {
    parts.push(`${years} year${years !== 1 ? 's' : ''}`);
  }
  if (months > 0) {
    parts.push(`${months} month${months !== 1 ? 's' : ''}`);
  }
  if (days > 0) {
    parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  }
  
  if (parts.length === 0) {
    return '0 days';
  }
  
  return parts.join(', ');
};

// Calculate expected service life remaining
export const calculateServiceLifeRemaining = (birthday, expectedLifespan = 70) => {
  if (!birthday) return null;
  
  try {
    const birthDate = new Date(birthday);
    const currentDate = new Date();
    
    // Validate birth date
    if (isNaN(birthDate.getTime()) || birthDate > currentDate) {
      return null;
    }
    
    // Calculate expected death date
    const expectedDeathDate = new Date(birthDate);
    expectedDeathDate.setFullYear(expectedDeathDate.getFullYear() + expectedLifespan);
    
    // If we've already passed the expected death date, return 0
    if (currentDate >= expectedDeathDate) {
      return {
        years: 0,
        months: 0,
        days: 0,
        isOverdue: true
      };
    }
    
    // Calculate remaining time
    let years = expectedDeathDate.getFullYear() - currentDate.getFullYear();
    let months = expectedDeathDate.getMonth() - currentDate.getMonth();
    let days = expectedDeathDate.getDate() - currentDate.getDate();
    
    // Adjust for negative days
    if (days < 0) {
      months--;
      // Get the last day of the previous month
      const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
      days += lastMonth.getDate();
    }
    
    // Adjust for negative months
    if (months < 0) {
      years--;
      months += 12;
    }
    
    return {
      years,
      months,
      days,
      isOverdue: false
    };
  } catch (error) {
    console.error('Error calculating service life remaining:', error);
    return null;
  }
};

// Format service life remaining for display
export const formatServiceLifeRemaining = (birthday, expectedLifespan = 70) => {
  const remaining = calculateServiceLifeRemaining(birthday, expectedLifespan);
  if (!remaining) return null;
  
  const { years, months, days, isOverdue } = remaining;
  
  if (isOverdue) {
    return 'Service life exceeded';
  }
  
  const parts = [];
  if (years > 0) {
    parts.push(`${years} year${years !== 1 ? 's' : ''}`);
  }
  if (months > 0) {
    parts.push(`${months} month${months !== 1 ? 's' : ''}`);
  }
  if (days > 0) {
    parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  }
  
  if (parts.length === 0) {
    return '0 days';
  }
  
  return parts.join(', ');
};
